import type { SiteLanguageCode } from "@/lib/language";

type Dict<T> = Record<SiteLanguageCode, T>;

export const navCopy: Dict<Record<string, string>> = {
  en: {
    Home: "Home",
    Services: "Services",
    Blog: "Blog",
    Work: "Work",
    About: "About",
    Careers: "Careers",
    Contact: "Contact",
  },
  hi: {
    Home: "होम",
    Services: "सेवाएँ",
    Blog: "ब्लॉग",
    Work: "कार्य",
    About: "हमारे बारे में",
    Careers: "करियर",
    Contact: "संपर्क",
  },
};

export const heroCopy = {
  en: {
    line1: "Build the",
    line2: "Future",
    line3: "with",
    line4: "DevDotCom",
    sub: "Enterprise-grade developer tools and services engineered for teams that ship fast.",
    ctaPrimary: "Get Started",
    ctaSecondary: "Explore Services",
    stats: [
      { title: "5+ products", description: "shipped to production" },
      { title: "20+ clients", description: "across sectors" },
      { title: "Gov ready", description: "compliance-first builds" },
      { title: "10+ awards", description: "and recognitions" },
    ],
    trusted:
      "Trusted by government and enterprise teams building production systems across India",
  },
  hi: {
    line1: "भविष्य बनाएँ",
    line2: "",
    line3: "DevDotCom",
    line4: "के साथ",
    sub: "तेज़ी से शिप करने वाली टीमें के लिए एंटरप्राइज़-ग्रेड डेवलपर टूल्स और सेवाएँ।",
    ctaPrimary: "शुरू करें",
    ctaSecondary: "सेवाएँ देखें",
    stats: [
      { title: "5+ उत्पाद", description: "प्रोडक्शन में शिप" },
      { title: "20+ क्लाइंट", description: "विभिन्न क्षेत्रों में" },
      { title: "सरकारी तैयार", description: "अनुपालन-प्रथम बिल्ड" },
      { title: "10+ पुरस्कार", description: "और सम्मान" },
    ],
    trusted:
      "भारत भर में प्रोडक्शन सिस्टम बनाने वाली सरकारी और एंटरप्राइज़ टीमों का विश्वास",
  },
} as const;

export const servicesCopy = {
  en: {
    title: "What We Solve",
    subtitle:
      "We eliminate complexity and deliver solutions that accelerate your development cycle.",
    close: "Close",
    learnMore: "Learn more about",
    cards: [
      {
        name: "Web Apps",
        description:
          "Product-grade web applications with clean architecture, fast load times, and interfaces your users actually enjoy.",
        highlights: [
          "Responsive product UI",
          "Auth, dashboards & workflows",
          "Performance-first delivery",
        ],
      },
      {
        name: "Cloud",
        description:
          "Reliable cloud foundations so you can ship features without babysitting infra, scaling, or deploy chaos.",
        highlights: [
          "Scalable cloud architecture",
          "CI/CD & environment parity",
          "Observability baked in",
        ],
      },
      {
        name: "Mobile",
        description:
          "Native-feel mobile experiences that stay consistent with your web product and ship on a shared engineering rhythm.",
        highlights: [
          "Cross-platform apps",
          "Shared design language",
          "Store-ready releases",
        ],
      },
      {
        name: "APIs",
        description:
          "Stable APIs and integrations that connect your stack cleanly - documented, versioned, and built for real traffic.",
        highlights: [
          "REST & event-driven APIs",
          "Third-party integrations",
          "Clear contracts & docs",
        ],
      },
      {
        name: "AI",
        description:
          "Practical AI features embedded into your product - assistants, automation, and insights that reduce busywork.",
        highlights: [
          "LLM-powered workflows",
          "Secure data handling",
          "Measurable product outcomes",
        ],
      },
      {
        name: "Support",
        description:
          "Ongoing partnership after launch - maintenance, iteration, and a team that stays accountable when production matters.",
        highlights: [
          "Post-launch ownership",
          "Priority issue response",
          "Continuous improvements",
        ],
      },
    ],
  },
  hi: {
    title: "हम क्या हल करते हैं",
    subtitle:
      "हम जटिलता हटाते हैं और ऐसे समाधान देते हैं जो आपके डेवलपमेंट चक्र को तेज़ करते हैं।",
    close: "बंद करें",
    learnMore: "और जानें",
    cards: [
      {
        name: "वेब ऐप्स",
        description:
          "क्लीन आर्किटेक्चर, तेज़ लोड और उपयोगी इंटरफ़ेस वाले प्रोडक्ट-ग्रेड वेब एप्लिकेशन।",
        highlights: [
          "रेस्पॉन्सिव प्रोडक्ट UI",
          "ऑथ, डैशबोर्ड और वर्कफ़्लो",
          "परफ़ॉर्मेंस-फ़र्स्ट डिलीवरी",
        ],
      },
      {
        name: "क्लाउड",
        description:
          "विश्वसनीय क्लाउड आधार ताकि आप इन्फ्रा की चिंता किए बिना फीचर्स शिप कर सकें।",
        highlights: [
          "स्केलेबल क्लाउड आर्किटेक्चर",
          "CI/CD और एनवायरनमेंट समानता",
          "ऑब्ज़र्वेबिलिटी अंतर्निहित",
        ],
      },
      {
        name: "मोबाइल",
        description:
          "नेटिव जैसा मोबाइल अनुभव जो आपके वेब प्रोडक्ट से मेल खाए और एक साथ शिप हो।",
        highlights: [
          "क्रॉस-प्लेटफ़ॉर्म ऐप्स",
          "साझा डिज़ाइन भाषा",
          "स्टोर-रेडी रिलीज़",
        ],
      },
      {
        name: "APIs",
        description:
          "स्थिर API और इंटीग्रेशन जो आपके स्टैक को साफ़ तरीके से जोड़ते हैं।",
        highlights: [
          "REST और इवेंट-ड्रिवन API",
          "थर्ड-पार्टी इंटीग्रेशन",
          "स्पष्ट कॉन्ट्रैक्ट और डॉक्स",
        ],
      },
      {
        name: "AI",
        description:
          "व्यावहारिक AI फीचर्स - असिस्टेंट, ऑटोमेशन और इनसाइट्स जो काम घटाएँ।",
        highlights: [
          "LLM-संचालित वर्कफ़्लो",
          "सुरक्षित डेटा हैंडलिंग",
          "मापने योग्य परिणाम",
        ],
      },
      {
        name: "सपोर्ट",
        description:
          "लॉन्च के बाद भी साझेदारी - मेंटनेंस, सुधार और ज़िम्मेदार टीम।",
        highlights: [
          "पोस्ट-लॉन्च स्वामित्व",
          "प्राथमिकता इश्यू रिस्पॉन्स",
          "निरंतर सुधार",
        ],
      },
    ],
  },
} as const;

export const techCopy = {
  en: {
    eyebrow: "Engineering stack",
    title: "Technologies we build with",
    subtitle:
      "Modern frameworks, cloud platforms, and data systems - chosen for reliability, speed, and long-term maintainability.",
    subtitleDesktop:
      "Modern frameworks, cloud platforms, and data systems - chosen for reliability, speed, and long-term maintainability across every product we ship.",
    cta: "View our work",
  },
  hi: {
    eyebrow: "इंजीनियरिंग स्टैक",
    title: "जिन तकनीकों से हम बनाते हैं",
    subtitle:
      "आधुनिक फ्रेमवर्क, क्लाउड प्लेटफ़ॉर्म और डेटा सिस्टम - विश्वसनीयता, गति और दीर्घकालिक मेंटेनेंस के लिए।",
    subtitleDesktop:
      "आधुनिक फ्रेमवर्क, क्लाउड प्लेटफ़ॉर्म और डेटा सिस्टम - हर प्रोडक्ट में विश्वसनीयता, गति और दीर्घकालिक मेंटेनेंस के लिए।",
    cta: "हमारा कार्य देखें",
  },
} as const;

export const productsCopy = {
  en: {
    title: "Developer Tools",
    subtitle:
      "Purpose-built tools that integrate seamlessly into your workflow - interactive, production-ready, and built for teams that ship.",
  },
  hi: {
    title: "डेवलपर टूल्स",
    subtitle:
      "आपके वर्कफ़्लो में सहज रूप से जुड़ने वाले टूल्स - इंटरैक्टिव, प्रोडक्शन-रेडी, और तेज़ शिपिंग टीमों के लिए।",
  },
} as const;

export const workCopy = {
  en: {
    title: "Teams ship with DevDotCom",
    subtitle:
      "Real products across government, EV, education, and enterprise - built to run in production.",
    cta: "Explore case studies",
  },
  hi: {
    title: "टीमें DevDotCom के साथ शिप करती हैं",
    subtitle:
      "सरकार, EV, शिक्षा और एंटरप्राइज़ में वास्तविक उत्पाद - प्रोडक्शन के लिए बनाए गए।",
    cta: "केस स्टडी देखें",
  },
} as const;

export const blogPreviewCopy = {
  en: {
    title: "Latest from the Blog",
    subtitle:
      "Insights, tutorials, and updates from our team of engineers and designers.",
    cta: "View All Posts",
    ctaMobile: "Explore All Articles",
  },
  hi: {
    title: "ब्लॉग से नवीनतम",
    subtitle:
      "हमारी इंजीनियर और डिज़ाइनर टीम से अंतर्दृष्टि, ट्यूटोरियल और अपडेट।",
    cta: "सभी पोस्ट देखें",
    ctaMobile: "सभी लेख देखें",
  },
} as const;

export const mobileDrawerCopy = {
  en: {
    contact: "Contact",
    preferChat:
      "Prefer to chat? Click Contact and we'll reply within one business day.",
  },
  hi: {
    contact: "संपर्क",
    preferChat:
      "चैट पसंद है? संपर्क पर क्लिक करें - हम एक कार्य दिवस में जवाब देंगे।",
  },
} as const;

export const techExtraCopy = {
  en: {
    byLayer: "By layer",
    stackWork: "How we put the stack to work",
    stackBlurb:
      "Four layers, one delivery model - from the interface down to infrastructure.",
  },
  hi: {
    byLayer: "परत के अनुसार",
    stackWork: "हम स्टैक को कैसे काम में लाते हैं",
    stackBlurb:
      "चार परतें, एक डिलीवरी मॉडल - इंटरफ़ेस से इन्फ्रास्ट्रक्चर तक।",
  },
} as const;

export const footerCopy = {
  en: {
    tagline: "Innovate. Collaborate. Dominate.",
    navigation: "Navigation",
    products: "Products",
    services: "Services",
    rights: "© 2026 DevDotCom. All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms & Conditions",
    productLinks: ["DevCLI", "DataSync", "SecureAuth", "FlowBuilder"],
    serviceLinks: [
      "Web Development",
      "Cloud Infrastructure",
      "Mobile Development",
      "API Integration",
      "AI & Automation",
      "Support & Maintenance",
    ],
    navLinks: ["Home", "Services", "Work", "About", "Careers", "Contact"],
  },
  hi: {
    tagline: "नवाचार। सहयोग। सफलता।",
    navigation: "नेविगेशन",
    products: "उत्पाद",
    services: "सेवाएँ",
    rights: "© 2026 DevDotCom. सर्वाधिकार सुरक्षित।",
    privacy: "गोपनीयता नीति",
    terms: "नियम और शर्तें",
    productLinks: [
      "DevCLI टूल",
      "डेटा सिंक",
      "सिक्योर ऑथ",
      "फ्लो बिल्डर",
    ],
    serviceLinks: [
      "वेब डेवलपमेंट",
      "क्लाउड इन्फ्रास्ट्रक्चर",
      "मोबाइल डेवलपमेंट",
      "API इंटीग्रेशन",
      "AI और ऑटोमेशन",
      "सपोर्ट और मेंटनेंस",
    ],
    navLinks: ["होम", "सेवाएँ", "कार्य", "हमारे बारे में", "करियर", "संपर्क"],
  },
} as const;

export function tNav(lang: SiteLanguageCode, label: string): string {
  return navCopy[lang][label] ?? label;
}

export const careersCopy = {
  en: {
    eyebrow: "Careers",
    title: "Build with a team that ships",
    subtitle:
      "We're looking for engineers and designers who care about craft, clarity, and long-term product quality.",
    location: "India · Remote-first",
    apply: "Apply",
    openTitle: "Don't see a fit?",
    openBody:
      "Send an open application - we read every note and keep strong candidates in mind for upcoming roles.",
    getInTouch: "Get in touch",
  },
  hi: {
    eyebrow: "करियर",
    title: "एक शिपिंग टीम के साथ बनाएँ",
    subtitle:
      "हम ऐसे इंजीनियर और डिज़ाइनर ढूँढते हैं जिन्हें क्राफ्ट, स्पष्टता और दीर्घकालिक प्रोडक्ट क्वालिटी की परवाह हो।",
    location: "भारत · रिमोट-फ़र्स्ट",
    apply: "आवेदन करें",
    openTitle: "उपयुक्त भूमिका नहीं दिखी?",
    openBody:
      "ओपन आवेदन भेजें - हम हर नोट पढ़ते हैं और आने वाली भूमिकाओं के लिए अच्छे उम्मीदवार ध्यान में रखते हैं।",
    getInTouch: "संपर्क करें",
  },
} as const;

export const contactCopy = {
  en: {
    eyebrow: "Contact",
    title: "Let's build something exceptional.",
    subtitle:
      "Tell us about your product, timeline, and goals. We'll reply with a clear next step - no fluff, just engineering partnership.",
    channels: "Direct channels",
    channelsBody: "Prefer email? Reach out anytime - we read every message.",
    email: "Email",
    location: "Location",
    locationValue: "India · Remote-first",
    response: "Response time",
    responseValue: "Within one business day",
    send: "Send message",
    name: "Name",
    namePlaceholder: "Your name",
    phone: "Contact number",
    phonePlaceholder: "+91 98765 43210",
    company: "Company / Project",
    companyPlaceholder: "Optional",
    service: "Service",
    servicePlaceholder: "e.g. Web Development",
    query: "Query / Interest",
    queryPlaceholder: "e.g. Pricing, timeline, demo",
    message: "Message",
    messagePlaceholder:
      "Hello DevDotCom team, I would like to enquire about...",
    emailPlaceholder: "you@company.com",
    successTitle: "Message sent successfully",
    successBody:
      "Thanks for reaching out. We've received your enquiry and will get back within one business day.",
    sendAnother: "Send another message",
    backHome: "Back to home",
    sending: "Sending…",
    sendAMessage: "Send a message",
    formSubtitle: "We typically respond within one business day.",
    whatHappensNext: "What to include",
    whatHappensNextBullets: [
      "The service or problem you want help with",
      "A reachable phone number",
      "Timeline or constraints (optional)",
    ],
    errorSend:
      "Something went wrong sending your message. Please try again or email us directly.",
  },
  hi: {
    eyebrow: "संपर्क",
    title: "कुछ असाधारण बनाएँ।",
    subtitle:
      "अपने प्रोडक्ट, टाइमलाइन और लक्ष्यों के बारे में बताएँ। हम स्पष्ट अगला कदम बताएँगे - बिना फालतू बात के, सिर्फ इंजीनियरिंग पार्टनरशिप।",
    channels: "सीधे चैनल",
    channelsBody: "ईमेल पसंद है? कभी भी लिखें - हम हर संदेश पढ़ते हैं।",
    email: "ईमेल",
    location: "स्थान",
    locationValue: "भारत · रिमोट-फ़र्स्ट",
    response: "जवाब का समय",
    responseValue: "एक कार्य दिवस के भीतर",
    send: "संदेश भेजें",
    name: "नाम",
    namePlaceholder: "आपका नाम",
    phone: "संपर्क नंबर",
    phonePlaceholder: "+91 98765 43210",
    company: "कंपनी / प्रोजेक्ट",
    companyPlaceholder: "वैकल्पिक",
    service: "सेवा",
    servicePlaceholder: "जैसे वेब डेवलपमेंट",
    query: "प्रश्न / रुचि",
    queryPlaceholder: "जैसे कीमत, टाइमलाइन, डेमो",
    message: "संदेश",
    messagePlaceholder:
      "नमस्ते DevDotCom टीम, मैं जानना चाहता/चाहती हूँ...",
    emailPlaceholder: "you@company.com",
    successTitle: "संदेश सफलतापूर्वक भेजा गया",
    successBody:
      "संपर्क करने के लिए धन्यवाद। हमें आपका अनुरोध मिल गया है और हम एक कार्य दिवस में जवाब देंगे।",
    sendAnother: "एक और संदेश भेजें",
    backHome: "होम पर वापस जाएँ",
    sending: "भेजा जा रहा है…",
    sendAMessage: "संदेश भेजें",
    formSubtitle: "हम आमतौर पर एक कार्य दिवस में जवाब देते हैं।",
    whatHappensNext: "क्या शामिल करें",
    whatHappensNextBullets: [
      "जिस सेवा या समस्या में मदद चाहिए",
      "एक संपर्क योग्य फ़ोन नंबर",
      "टाइमलाइन या बाधाएँ (वैकल्पिक)",
    ],
    errorSend:
      "संदेश भेजने में कुछ गलत हुआ। कृपया फिर कोशिश करें या हमें सीधे ईमेल करें।",
  },
} as const;

export const servicesHeaderCopy = {
  en: {
    quickJump: "Quick jump",
    ourWork: "Our work",
    viewAllServices: "View all services",
    viewAllProjects: "View all projects",
    serviceJump: [
      {
        title: "Web & App Development",
        subtitle: "Modern web and mobile applications",
      },
      {
        title: "AI & Automation",
        subtitle: "Intelligent systems and automation",
      },
      {
        title: "DevOps & Cloud",
        subtitle: "Infrastructure and deployment",
      },
      {
        title: "Support & Maintenance",
        subtitle: "Ongoing support services",
      },
    ],
  },
  hi: {
    quickJump: "त्वरित जाएँ",
    ourWork: "हमारा कार्य",
    viewAllServices: "सभी सेवाएँ देखें",
    viewAllProjects: "सभी प्रोजेक्ट देखें",
    serviceJump: [
      {
        title: "वेब और ऐप डेवलपमेंट",
        subtitle: "आधुनिक वेब और मोबाइल एप्लिकेशन",
      },
      {
        title: "AI और ऑटोमेशन",
        subtitle: "बुद्धिमान सिस्टम और ऑटोमेशन",
      },
      {
        title: "DevOps और क्लाउड",
        subtitle: "इन्फ्रास्ट्रक्चर और डिप्लॉयमेंट",
      },
      {
        title: "सपोर्ट और मेंटनेंस",
        subtitle: "निरंतर सपोर्ट सेवाएँ",
      },
    ],
  },
} as const;

export const servicesPageCopy = {
  en: {
    heroLine1: "Crafting digital",
    heroLine2: "solutions with today's",
    heroLine3: "tech",
    heroExperience: "experience",
    heroSub:
      "Engineering-first services - from web apps to cloud, built to ship.",
    startProject: "Start a Project",
    tapHint: "tap to interact ✨",
    ctaBadge: "Start Building Today",
    ctaTitleBefore: "Transform Your",
    ctaTitleAccent: "Vision Into Reality",
    ctaSubtitle:
      "Join {count}+ companies who have accelerated their digital transformation with DevDotCom. Get a free demo and see how we can help.",
    ctaPrimary: "Get Free Demo",
    watchDemo: "Watch Demo Video",
    freeConsultation: "Free Consultation",
    noCommitment: "No Commitment",
    fastResponse: "Fast Response",
    watch2Min: "Watch our 2-minute demo",
    platformOverview: "Platform Overview",
    videoMeta: "2:30 • HD Quality",
    demoBadge: "Demo",
    demoComingSoon: "Demo video coming soon",
    contactForLive: "Contact us for a live demo",
    requestLiveDemo: "Request Live Demo",
    startProjectQuery: "Start a Project · Services",
    startProjectMessage:
      "Hello DevDotCom team, I would like to enquire about starting a project.",
    freeDemoQuery: "Free Demo Request",
    freeDemoMessage:
      "Hello DevDotCom team, I would like to enquire about a free demo.",
    liveDemoQuery: "Live Demo Request",
    liveDemoMessage:
      "Hello DevDotCom team, I would like to enquire about a live demo.",
  },
  hi: {
    heroLine1: "आज की तकनीक से",
    heroLine2: "डिजिटल समाधान",
    heroLine3: "गढ़ते हैं",
    heroExperience: "अनुभव",
    heroSub:
      "इंजीनियरिंग-प्रथम सेवाएँ - वेब ऐप्स से क्लाउड तक, शिप करने के लिए बनी।",
    startProject: "प्रोजेक्ट शुरू करें",
    tapHint: "इंटरैक्ट करने के लिए टैप करें ✨",
    ctaBadge: "आज ही बनाना शुरू करें",
    ctaTitleBefore: "अपने विज़न को",
    ctaTitleAccent: "वास्तविकता में बदलें",
    ctaSubtitle:
      "{count}+ कंपनियों से जुड़ें जिन्होंने DevDotCom के साथ डिजिटल परिवर्तन तेज़ किया। मुफ़्त डेमो लें और देखें हम कैसे मदद कर सकते हैं।",
    ctaPrimary: "मुफ़्त डेमो लें",
    watchDemo: "डेमो वीडियो देखें",
    freeConsultation: "मुफ़्त परामर्श",
    noCommitment: "कोई बाध्यता नहीं",
    fastResponse: "तेज़ जवाब",
    watch2Min: "हमारा 2-मिनट डेमो देखें",
    platformOverview: "प्लेटफ़ॉर्म अवलोकन",
    videoMeta: "2:30 • HD गुणवत्ता",
    demoBadge: "डेमो",
    demoComingSoon: "डेमो वीडियो जल्द आ रहा है",
    contactForLive: "लाइव डेमो के लिए संपर्क करें",
    requestLiveDemo: "लाइव डेमो अनुरोध करें",
    startProjectQuery: "प्रोजेक्ट शुरू करें · सेवाएँ",
    startProjectMessage:
      "नमस्ते DevDotCom टीम, मैं एक प्रोजेक्ट शुरू करने के बारे में जानना चाहता/चाहती हूँ।",
    freeDemoQuery: "मुफ़्त डेमो अनुरोध",
    freeDemoMessage:
      "नमस्ते DevDotCom टीम, मैं मुफ़्त डेमो के बारे में जानना चाहता/चाहती हूँ।",
    liveDemoQuery: "लाइव डेमो अनुरोध",
    liveDemoMessage:
      "नमस्ते DevDotCom टीम, मैं लाइव डेमो के बारे में जानना चाहता/चाहती हूँ।",
  },
} as const;

export const servicesCatalogCopy = {
  en: {
    eyebrow: "Services",
    title: "What we build for you",
    subtitle:
      "Pick a category to explore services, or jump straight to the one you need.",
    viewAll: "View all {count} services",
    navLabel: "Service categories",
  },
  hi: {
    eyebrow: "सेवाएँ",
    title: "हम आपके लिए क्या बनाते हैं",
    subtitle:
      "सेवाएँ देखने के लिए श्रेणी चुनें, या सीधे अपनी ज़रूरी सेवा पर जाएँ।",
    viewAll: "सभी {count} सेवाएँ देखें",
    navLabel: "सेवा श्रेणियाँ",
  },
} as const;

export const servicesWorkSectionCopy = {
  en: {
    eyebrow: "Our work",
    title: "See what we've shipped",
    subtitle:
      "Real projects across government, EV, and enterprise - built to scale in production.",
    viewAll: "View all work",
  },
  hi: {
    eyebrow: "हमारा कार्य",
    title: "देखें हमने क्या शिप किया",
    subtitle:
      "सरकार, EV और एंटरप्राइज़ में वास्तविक प्रोजेक्ट - प्रोडक्शन में स्केल करने के लिए।",
    viewAll: "सारा कार्य देखें",
  },
} as const;

export const productsCardsCopy = {
  en: {
    workspace: {
      title: "Your team's workspace",
      body: "Organize projects, drafts, and shared assets in one place - built for shipping together.",
    },
    optimize: {
      title: "Optimize and ship",
      body: "Compress payloads, trim cold starts, and keep every release lean.",
    },
    progress: {
      title: "Progress updates",
      body: "Stay in the loop with live deploy status and review alerts.",
    },
    handoff: {
      title: "Seamless developer handoff",
      body: "Share secure links, pick your SDK, and drop production-ready snippets into the codebase.",
    },
    integration: {
      title: "Integration",
      body: "Plug DevDotCom into the tools your team already lives in.",
    },
    ui: {
      dashboard: "Dashboard",
      drafts: "My Drafts",
      searchPlaceholder: "Search projects…",
      searchProjects: "Search projects…",
      newProject: "New project",
      newShort: "New",
      acmeProd: "Acme Prod",
      openedAcme: "Opened Acme Prod",
      openedDashboard: "Opened Dashboard",
      openedDrafts: "Opened My Drafts",
      openedItem: "Opened {name}",
      projectCreated: "Project created",
      starred: "Starred",
      unstarred: "Removed from starred",
      noAssets: "No assets match “{query}”",
      viewLinkedIn: "View LinkedIn",
      closeProfile: "Close profile",
      bundleSize: "Bundle size",
      smallerBaseline: "{pct}% smaller than baseline",
      clickReoptimize: "Click to re-optimize",
      deployReady: "Deploy ready",
      reviewRequested: "Review requested",
      syncHealthy: "Sync healthy",
      clickAlerts: "Click to open alerts",
      assetConfig: "Asset configuration",
      assetLink: "Asset link",
      format: "Format",
      sdkEmbed: "SDK embed",
      cdnScript: "CDN script",
      restApi: "REST API",
      copied: "Copied",
      copy: "Copy",
      designSystem: "Design system",
      authScreens: "Auth screens",
      engDeploys: "#eng-deploys",
    },
  },
  hi: {
    workspace: {
      title: "आपकी टीम का वर्कस्पेस",
      body: "प्रोजेक्ट, ड्राफ़्ट और साझा एसेट एक जगह व्यवस्थित करें - साथ मिलकर शिप करने के लिए।",
    },
    optimize: {
      title: "अनुकूलित करें और शिप करें",
      body: "पेलोड संपीड़ित करें, कोल्ड स्टार्ट घटाएँ, और हर रिलीज़ हल्की रखें।",
    },
    progress: {
      title: "प्रगति अपडेट",
      body: "लाइव डिप्लॉय स्थिति और रिव्यू अलर्ट से जुड़े रहें।",
    },
    handoff: {
      title: "सहज डेवलपर हैंडऑफ़",
      body: "सुरक्षित लिंक साझा करें, SDK चुनें, और प्रोडक्शन-रेडी स्निपेट कोडबेस में डालें।",
    },
    integration: {
      title: "इंटीग्रेशन",
      body: "DevDotCom को उन टूल्स से जोड़ें जिनमें आपकी टीम पहले से काम करती है।",
    },
    ui: {
      dashboard: "डैशबोर्ड",
      drafts: "मेरे ड्राफ़्ट",
      searchPlaceholder: "प्रोजेक्ट खोजें…",
      searchProjects: "प्रोजेक्ट खोजें…",
      newProject: "नया प्रोजेक्ट",
      newShort: "नया",
      acmeProd: "Acme Prod",
      openedAcme: "Acme Prod खोला",
      openedDashboard: "डैशबोर्ड खोला",
      openedDrafts: "मेरे ड्राफ़्ट खोले",
      openedItem: "{name} खोला",
      projectCreated: "प्रोजेक्ट बनाया गया",
      starred: "स्टार किया",
      unstarred: "स्टार से हटाया",
      noAssets: "“{query}” से कोई एसेट मेल नहीं खाता",
      viewLinkedIn: "LinkedIn देखें",
      closeProfile: "प्रोफ़ाइल बंद करें",
      bundleSize: "बंडल आकार",
      smallerBaseline: "बेसलाइन से {pct}% छोटा",
      clickReoptimize: "फिर अनुकूलित करने के लिए क्लिक करें",
      deployReady: "डिप्लॉय तैयार",
      reviewRequested: "रिव्यू अनुरोधित",
      syncHealthy: "सिंक स्वस्थ",
      clickAlerts: "अलर्ट खोलने के लिए क्लिक करें",
      assetConfig: "एसेट कॉन्फ़िगरेशन",
      assetLink: "एसेट लिंक",
      format: "फ़ॉर्मैट",
      sdkEmbed: "SDK एम्बेड",
      cdnScript: "CDN स्क्रिप्ट",
      restApi: "REST API",
      copied: "कॉपी हो गया",
      copy: "कॉपी",
      designSystem: "डिज़ाइन सिस्टम",
      authScreens: "ऑथ स्क्रीन",
      engDeploys: "#eng-deploys",
    },
  },
} as const;

export const serviceCategoryCopy = {
  en: {
    learnMore: "Learn more",
    whatsIncluded: "What's Included",
    getStarted: "Get Started",
    servicesBadge: "{count} Services",
    enterpriseReady: "Enterprise Ready",
    support247: "24/7 Support",
    ourServices: "Our {category} Services",
    clickHint:
      "Click on any service to learn more about how we can help you achieve your goals.",
    notFound: "Category Not Found",
    backToServices: "Back to Services",
    enquireAbout:
      "Hello DevDotCom team, I would like to enquire about {service}.",
  },
  hi: {
    learnMore: "और जानें",
    whatsIncluded: "क्या शामिल है",
    getStarted: "शुरू करें",
    servicesBadge: "{count} सेवाएँ",
    enterpriseReady: "एंटरप्राइज़ तैयार",
    support247: "24/7 सपोर्ट",
    ourServices: "हमारी {category} सेवाएँ",
    clickHint:
      "किसी भी सेवा पर क्लिक करें और जानें कि हम आपके लक्ष्यों को कैसे हासिल करने में मदद कर सकते हैं।",
    notFound: "श्रेणी नहीं मिली",
    backToServices: "सेवाओं पर वापस जाएँ",
    enquireAbout:
      "नमस्ते DevDotCom टीम, मैं {service} के बारे में जानना चाहता/चाहती हूँ।",
  },
} as const;

export const notFoundCopy = {
  en: {
    title: "Oops! Page Not Found",
    body: "The page you're looking for seems to have wandered off. Don't worry, even our cat couldn't find it!",
    goHome: "Go Home",
    goBack: "Go Back",
    explore: "Or explore these pages:",
    Services: "Services",
    Products: "Products",
    About: "About Us",
    Contact: "Contact",
  },
  hi: {
    title: "ओह! पेज नहीं मिला",
    body: "आप जो पेज ढूँढ रहे हैं वह कहीं खो गया लगता है। चिंता न करें - हमारी बिल्ली भी उसे नहीं ढूँढ पाई!",
    goHome: "होम जाएँ",
    goBack: "वापस जाएँ",
    explore: "या ये पेज देखें:",
    Services: "सेवाएँ",
    Products: "उत्पाद",
    About: "हमारे बारे में",
    Contact: "संपर्क",
  },
} as const;

export const headerSearchCopy = {
  en: { placeholder: "Search services…" },
  hi: { placeholder: "सेवाएँ खोजें…" },
} as const;

export const aboutHeroCopy = {
  en: {
    eyebrow: "About DevDotCom",
    title: "We build software that powers progress.",
    p1: "Thoughtful engineering focused on real-world impact - from SaaS platforms to production systems that scale with the teams that depend on them.",
    p2: "Built by engineers with experience across startups, enterprises, and government - shipping systems teams can rely on, long after launch day.",
    journey: "Our Journey",
    who: "Who we are",
  },
  hi: {
    eyebrow: "DevDotCom के बारे में",
    title: "हम ऐसा सॉफ़्टवेयर बनाते हैं जो प्रगति को शक्ति देता है।",
    p1: "वास्तविक प्रभाव पर केंद्रित सोच-समझकर इंजीनियरिंग - SaaS प्लेटफ़ॉर्म से लेकर प्रोडक्शन सिस्टम तक जो टीमों के साथ स्केल करें।",
    p2: "स्टार्टअप, एंटरप्राइज़ और सरकार में अनुभव वाले इंजीनियरों द्वारा - लॉन्च के बहुत बाद तक भरोसेमंद सिस्टम।",
    journey: "हमारी यात्रा",
    who: "हम कौन हैं",
  },
} as const;

export const workPageCopy = {
  en: {
    count: "shipped projects",
    title: "Selected Work",
    subtitle:
      "A running log of the systems we've designed, built, and put into production.",
    all: "All",
    empty: "No projects in this category yet.",
  },
  hi: {
    count: "शिप किए गए प्रोजेक्ट",
    title: "चयनित कार्य",
    subtitle:
      "जिन सिस्टम को हमने डिज़ाइन, बिल्ड और प्रोडक्शन में उतारा - उनकी चलती सूची।",
    all: "सभी",
    empty: "इस श्रेणी में अभी कोई प्रोजेक्ट नहीं है।",
  },
} as const;

export const heroMorphCopy = {
  en: {
    software: "Software Development",
    cloud: "Cloud Infrastructure",
    ai: "AI & Automation",
    mobile: "App Development",
    support: "Support & Maintenance",
  },
  hi: {
    software: "सॉफ़्टवेयर डेवलपमेंट",
    cloud: "क्लाउड इन्फ्रास्ट्रक्चर",
    ai: "AI और ऑटोमेशन",
    mobile: "ऐप डेवलपमेंट",
    support: "सपोर्ट और मेंटनेंस",
  },
} as const;

export const techStackGroupCopy = {
  en: {
    labels: ["Frontend", "Backend", "Data & AI", "Cloud & DevOps"],
    blurbs: [
      "Interfaces that feel native and stay fast.",
      "APIs and services built for real traffic.",
      "Reliable stores, queues, and learning systems.",
      "Shipped on containers, orchestrated at scale.",
    ],
  },
  hi: {
    labels: ["फ्रंटएंड", "बैकएंड", "डेटा और AI", "क्लाउड और DevOps"],
    blurbs: [
      "इंटरफ़ेस जो नेटिव लगें और तेज़ रहें।",
      "वास्तविक ट्रैफ़िक के लिए API और सेवाएँ।",
      "विश्वसनीय स्टोर, कतारें और लर्निंग सिस्टम।",
      "कंटेनर पर शिप, स्केल पर ऑर्केस्ट्रेटेड।",
    ],
  },
} as const;

export const projectDetailCopy = {
  en: {
    live: "Live in production",
    caseStudy: "Case study",
    visit: "Visit live project",
    industry: "Industry",
    solution: "Solution",
    year: "Year",
    duration: "Duration",
    challenge: "The Challenge",
    approach: "Our Approach",
    impact: "Impact",
    techStack: "Tech Stack",
    gallery: "Project Gallery",
    gallerySub: "Screens, flows, and interfaces from the build - tap any frame to expand.",
    assets: "assets",
    previous: "Previous",
    next: "Next",
  },
  hi: {
    live: "प्रोडक्शन में लाइव",
    caseStudy: "केस स्टडी",
    visit: "लाइव प्रोजेक्ट देखें",
    industry: "उद्योग",
    solution: "समाधान",
    year: "वर्ष",
    duration: "अवधि",
    challenge: "चुनौती",
    approach: "हमारा दृष्टिकोण",
    impact: "प्रभाव",
    techStack: "टेक स्टैक",
    gallery: "प्रोजेक्ट गैलरी",
    gallerySub: "बिल्ड से स्क्रीन, फ्लो और इंटरफ़ेस - बड़ा देखने के लिए टैप करें।",
    assets: "एसेट्स",
    previous: "पिछला",
    next: "अगला",
  },
} as const;

export const projectCardCopy = {
  en: { live: "Live", caseStudy: "Case study", view: "view" },
  hi: { live: "लाइव", caseStudy: "केस स्टडी", view: "देखें" },
} as const;

export const legalChromeCopy = {
  en: {
    eyebrow: "Legal",
    privacyTitle: "Privacy Policy",
    termsTitle: "Terms & Conditions",
    lastUpdated: "Last updated: July 12, 2026",
    privacyHeadings: {
      overview: "Overview",
      collect: "Information we collect",
      use: "How we use information",
      sharing: "Sharing",
      retention: "Retention",
      choices: "Your choices",
      contact: "Contact",
    },
    termsHeadings: {
      agreement: "Agreement",
      services: "Services & proposals",
      ip: "Intellectual property",
      acceptable: "Acceptable use",
      disclaimers: "Disclaimers",
      thirdParty: "Third-party links",
      changes: "Changes",
      contact: "Contact",
    },
    contactLink: "Contact",
  },
  hi: {
    eyebrow: "कानूनी",
    privacyTitle: "गोपनीयता नीति",
    termsTitle: "नियम और शर्तें",
    lastUpdated: "अंतिम अपडेट: 12 जुलाई 2026",
    privacyHeadings: {
      overview: "अवलोकन",
      collect: "हम जो जानकारी एकत्र करते हैं",
      use: "जानकारी का उपयोग कैसे करते हैं",
      sharing: "साझा करना",
      retention: "अवधारण",
      choices: "आपके विकल्प",
      contact: "संपर्क",
    },
    termsHeadings: {
      agreement: "सहमति",
      services: "सेवाएँ और प्रस्ताव",
      ip: "बौद्धिक संपदा",
      acceptable: "स्वीकार्य उपयोग",
      disclaimers: "अस्वीकरण",
      thirdParty: "थर्ड-पार्टी लिंक",
      changes: "परिवर्तन",
      contact: "संपर्क",
    },
    contactLink: "संपर्क",
  },
} as const;

export const blogCardCopy = {
  en: {
    readMore: "Read more",
    min: "min",
    minRead: "min read",
  },
  hi: {
    readMore: "और पढ़ें",
    min: "मिनट",
    minRead: "मिनट पढ़ें",
  },
} as const;

export const privacyBodyCopy = {
  en: {
    overview:
      "DevDotCom (“we”, “us”) respects your privacy. This policy explains what information we collect when you use {domain}, our contact forms, and related services, and how we use it.",
    collect: [
      "Contact details you submit (name, email, phone, company, message).",
      "Usage data such as pages visited, device type, and approximate location derived from IP address.",
      "Messages you send through our on-site chatbot or support flows.",
    ],
    use: [
      "To respond to enquiries and provide requested services.",
      "To improve our website, products, and support experience.",
      "To communicate about projects, proposals, or updates you request.",
      "To maintain security and prevent abuse.",
    ],
    sharing:
      "We do not sell your personal information. We may share data with trusted processors (for example hosting, analytics, or email delivery) solely to operate our business, or when required by law.",
    retention:
      "We keep enquiry and project-related records for as long as needed to fulfill the purpose you contacted us for, and to meet legal or accounting requirements.",
    choicesBefore:
      "You may request access, correction, or deletion of personal information we hold about you by emailing",
    choicesAfter: ".",
    contactBefore: "Questions about this policy:",
    contactOr: "Or visit our",
    contactAfter: "page.",
  },
  hi: {
    overview:
      "DevDotCom (“हम”) आपकी गोपनीयता का सम्मान करता है। यह नीति बताती है कि जब आप {domain}, हमारे संपर्क फ़ॉर्म और संबंधित सेवाओं का उपयोग करते हैं तो हम कौन-सी जानकारी एकत्र करते हैं, और उसका उपयोग कैसे करते हैं।",
    collect: [
      "आपके द्वारा सबमिट किए गए संपर्क विवरण (नाम, ईमेल, फ़ोन, कंपनी, संदेश)।",
      "उपयोग डेटा जैसे देखे गए पेज, डिवाइस प्रकार, और IP पते से अनुमानित स्थान।",
      "ऑन-साइट चैटबॉट या सपोर्ट फ़्लो के ज़रिए आपके भेजे गए संदेश।",
    ],
    use: [
      "पूछताछ का जवाब देने और अनुरोधित सेवाएँ प्रदान करने के लिए।",
      "हमारी वेबसाइट, उत्पाद और सपोर्ट अनुभव सुधारने के लिए।",
      "आपके अनुरोधित प्रोजेक्ट, प्रस्ताव या अपडेट के बारे में संवाद के लिए।",
      "सुरक्षा बनाए रखने और दुरुपयोग रोकने के लिए।",
    ],
    sharing:
      "हम आपकी व्यक्तिगत जानकारी नहीं बेचते। हम भरोसेमंद प्रोसेसरों (जैसे होस्टिंग, एनालिटिक्स या ईमेल डिलीवरी) के साथ केवल व्यवसाय चलाने के लिए, या कानून द्वारा आवश्यक होने पर डेटा साझा कर सकते हैं।",
    retention:
      "हम पूछताछ और प्रोजेक्ट-संबंधी रिकॉर्ड तब तक रखते हैं जब तक आपके संपर्क के उद्देश्य को पूरा करने और कानूनी या लेखांकन आवश्यकताओं को पूरा करने के लिए ज़रूरी हो।",
    choicesBefore:
      "आपके बारे में हमारे पास मौजूद व्यक्तिगत जानकारी तक पहुँच, सुधार या हटाने का अनुरोध आप ईमेल करके कर सकते हैं",
    choicesAfter: "।",
    contactBefore: "इस नीति के बारे में प्रश्न:",
    contactOr: "या हमारे",
    contactAfter: "पेज पर जाएँ।",
  },
} as const;

export const termsBodyCopy = {
  en: {
    agreement:
      "By accessing {domain} or engaging DevDotCom for services, you agree to these terms. If you do not agree, please do not use the site.",
    services:
      "Descriptions of products and services on this website are for general information. Binding scope, fees, timelines, and deliverables are defined in a written proposal, statement of work, or contract between you and DevDotCom.",
    ip: "Site content, branding, and materials are owned by DevDotCom or our licensors unless otherwise stated. Client project IP is governed by the applicable contract. You may not copy or reuse our site materials without prior written consent.",
    acceptable: [
      "Do not misuse the site, chatbot, or forms to spam or attack systems.",
      "Do not submit unlawful, harmful, or infringing content.",
      "Do not attempt to access non-public systems without authorization.",
    ],
    disclaimers:
      "The website is provided “as is.” We do not warrant uninterrupted availability or that content is error-free. To the fullest extent permitted by law, DevDotCom is not liable for indirect or consequential damages arising from use of the site.",
    thirdParty:
      "Links to external sites are provided for convenience. We are not responsible for third-party content or practices.",
    changes:
      "We may update these terms from time to time. Continued use of the site after changes constitutes acceptance of the revised terms.",
    contactBefore: "Questions:",
    contactOr: "Or use our",
    contactAfter: "page.",
  },
  hi: {
    agreement:
      "{domain} तक पहुँचकर या सेवाओं के लिए DevDotCom से जुड़कर, आप इन नियमों से सहमत होते हैं। यदि आप सहमत नहीं हैं, तो कृपया साइट का उपयोग न करें।",
    services:
      "इस वेबसाइट पर उत्पादों और सेवाओं के विवरण सामान्य जानकारी के लिए हैं। बाध्यकारी दायरा, शुल्क, समयसीमा और डिलिवरेबल आपके और DevDotCom के बीच लिखित प्रस्ताव, कार्य-विवरण या अनुबंध में परिभाषित होते हैं।",
    ip: "साइट सामग्री, ब्रांडिंग और सामग्री DevDotCom या हमारे लाइसेंसदाताओं की संपत्ति है जब तक अन्यथा न कहा जाए। क्लाइंट प्रोजेक्ट IP लागू अनुबंध द्वारा शासित होता है। पूर्व लिखित सहमति के बिना आप हमारी साइट सामग्री की नकल या पुन: उपयोग नहीं कर सकते।",
    acceptable: [
      "स्पैम या सिस्टम पर हमले के लिए साइट, चैटबॉट या फ़ॉर्म का दुरुपयोग न करें।",
      "अवैध, हानिकारक या उल्लंघनकारी सामग्री सबमिट न करें।",
      "बिना अधिकृति गैर-सार्वजनिक सिस्टम तक पहुँचने का प्रयास न करें।",
    ],
    disclaimers:
      "वेबसाइट “जैसी है” प्रदान की जाती है। हम निर्बाध उपलब्धता या त्रुटि-मुक्त सामग्री की गारंटी नहीं देते। कानून द्वारा अनुमत अधिकतम सीमा तक, साइट के उपयोग से उत्पन्न अप्रत्यक्ष या परिणामी क्षति के लिए DevDotCom उत्तरदायी नहीं है।",
    thirdParty:
      "बाहरी साइटों के लिंक सुविधा के लिए दिए गए हैं। हम थर्ड-पार्टी सामग्री या प्रथाओं के लिए जिम्मेदार नहीं हैं।",
    changes:
      "हम समय-समय पर इन नियमों को अपडेट कर सकते हैं। परिवर्तनों के बाद साइट का निरंतर उपयोग संशोधित नियमों की स्वीकृति माना जाता है।",
    contactBefore: "प्रश्न:",
    contactOr: "या हमारे",
    contactAfter: "पेज का उपयोग करें।",
  },
} as const;
